import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { IRedirectMessageRef, toast, TRedirectStatus } from "@/components";
import { useAuth, useI18n } from "@/context";

import { resetPasswordApi } from "./reset-password.api";
import { IResetPasswordHooks } from "./reset-password.interface";
import { resetPasswordSchema } from "./reset-password.schema";
import {
  IResetPasswordFormValues,
  IResetPasswordRequest,
} from "./reset-password.types";

export const useResetPassword = (): IResetPasswordHooks => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { handleAuthenticate } = useAuth();
  const [resetStatus, setResetStatus] =
    React.useState<TRedirectStatus>("loading");

  const redirectMessageRef = React.useRef<IRedirectMessageRef>(null);

  const form = useForm<IResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
      token: token || "",
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (data: IResetPasswordRequest) =>
      resetPasswordApi.resetPassword(data),
    onSuccess: (data) => {
      handleAuthenticate(
        {
          userId: data.userId,
          email: data.email,
          name: data.name,
          userPicture: data.userPicture,
          token: data.token,
          userType: "web",
        },
        "api"
      );
      setResetStatus("success");
      toast({
        title: t("generic.success"),
        description: t("generic.redirecting"),
      });
    },
    onError: (error: Error) => {
      setResetStatus("error");
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleResetPassword = React.useCallback(
    async (data: IResetPasswordFormValues) => {
      if (!token) return;
      return resetPasswordMutation.mutate({
        token: token || "",
        newPassword: data.password,
      });
    },
    [token, resetPasswordMutation]
  );

  React.useEffect(() => {
    if (!token) {
      return navigate("/login");
    }
  }, [token, navigate]);

  React.useEffect(() => {
    if (resetStatus !== "loading") {
      redirectMessageRef.current?.handleMessage();
    }
  }, [resetStatus]);

  const resetData = React.useMemo(() => {
    return {
      success: {
        message: t("auth.resetPasswordSuccess"),
        function: () => navigate("/dashboard"),
        time: 5,
      },
      error: {
        message: t("auth.resetPasswordError"),
        function: () => navigate("/login"),
        time: 10,
      },
      loading: {
        message: t("texts.loading"),
        function: () => {},
        time: 10,
      },
    };
  }, [t, navigate]);

  return {
    form,
    handleResetPassword,
    resetStatus,
    redirectMessageRef,
    resetData,
  };
};
